import { useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import ReCAPTCHA from 'react-google-recaptcha';
import { toast, Toaster } from 'react-hot-toast';
import { AnimatePresence, motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';

const schema = z.object({
  firstName: z.string().min(1, 'First name is required'),
  lastName: z.string().min(1, 'Last name is required'),
  email: z.string().email('A valid email is required'),
  subject: z.string().optional(),
  message: z.string().min(1, 'Message is required'),
  website: z.string().optional(),
});

export default function ContactUsPage() {
  const [submitted, setSubmitted] = useState(false);
  const recaptchaRef = useRef();

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: zodResolver(schema),
  });

  const onSubmit = async (data) => {
    const token = recaptchaRef.current.getValue();
    recaptchaRef.current.reset();

    if (!token) {
      toast.error('Captcha failed. Please try again.');
      return;
    }

    try {
      const response = await fetch(`${import.meta.env.VITE_API_BASE_URL}/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...data, token }),
      });

      if (response.ok) {
        toast.success('Thank you! Your message has been sent.');
        reset();
        setSubmitted(true);
      } else {
        let errorMsg = 'Something went wrong.';
        try {
          const result = await response.json();
          errorMsg = result.error || errorMsg;
        } catch (e) {
          console.error('Failed to parse JSON response:', e);
          const text = await response.text();
          errorMsg = text || errorMsg;
        }
        toast.error(errorMsg);
      }
    } catch (err) {
      toast.error('An error occurred. Please try again.');
      console.error(err);
    }
  };

  return (
    <>
      <Helmet>
        <title>Contact | CovePoint Development</title>
        <meta
          name="description"
          content="Reach out to the CovePoint team for residential land development opportunities and inquiries."
        />
        <meta property="og:title" content="Contact Us | CovePoint Development" />
        <meta property="og:description" content="Get in touch with CovePoint for residential development opportunities and inquiries." />
        <meta property="og:image" content="https://covepoint.com/share.jpg" />
        <meta property="og:url" content="https://covepoint.com/contact" />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <link rel="canonical" href="https://covepoint.com/contact" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "ContactPage",
            "name": "Contact CovePoint",
            "url": "https://covepoint.com/contact",
            "description": "Reach out to CovePoint Development Group for residential land development inquiries."
          })}
        </script>
      </Helmet>

      <div
        className="relative min-h-screen w-full overflow-y-auto px-6 sm:px-10 md:px-20 lg:px-32 bg-cover bg-center pt-[50px] md:pt-50"
        style={{ backgroundImage: `url('/hero.jpg')` }}
      >
        <Toaster position="top-right" />

        <div className="max-w-7xl w-full flex flex-col md:flex-row items-center md:items-start justify-center md:justify-between gap-10 py-12 md:py-24">

          <div className="text-white max-w-xl text-center md:text-left md:pt-10 px-2">
            <h1 className="text-3xl md:text-5xl font-bold mb-4 leading-tight">
              Connect with CovePoint<br />Development Group
            </h1>
            <p className="text-lg text-white/90">
              For additional information, please reach out and we will contact you at our earliest convenience.
            </p>
          </div>

          <div className="bg-white/60 backdrop-blur-lg rounded-xl p-6 sm:p-8 w-full max-w-sm shadow-lg min-h-[590px] sm:min-h-[540px] md:min-h-[520px] flex flex-col justify-between">
            <AnimatePresence mode="wait">
              {submitted ? (
                <motion.div
                  key="success"
                  initial={{ opacity: 0, y: 40 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 40 }}
                  transition={{ duration: 0.6, ease: 'easeOut' }}
                  className="text-center space-y-4 text-covenavy"
                >
                  <div className="text-coveblue text-6xl">✓</div>
                  <h2 className="text-xl font-semibold">Message Sent!</h2>
                  <p className="text-gray-700">We'll be in touch shortly.</p>
                </motion.div>
              ) : (
                <motion.form
                  key="form"
                  onSubmit={handleSubmit(onSubmit)}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 20 }}
                  transition={{ duration: 0.5, ease: 'easeOut' }}
                  className="flex flex-col h-full"
                >
                  <input type="hidden" {...register('website')} />

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">First Name *</label>
                      <input {...register('firstName')} className="input" aria-required="true" aria-invalid={!!errors.firstName} autoFocus />
                      {errors.firstName && <p className="text-red-500 text-sm">{errors.firstName.message}</p>}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Last Name *</label>
                      <input {...register('lastName')} className="input" aria-required="true" aria-invalid={!!errors.lastName} />
                      {errors.lastName && <p className="text-red-500 text-sm">{errors.lastName.message}</p>}
                    </div>
                    <div className="sm:col-span-2">
                      <label className="block text-sm font-medium text-gray-700">Email *</label>
                      <input type="email" {...register('email')} className="input w-full" aria-required="true" aria-invalid={!!errors.email} />
                      {errors.email && <p className="text-red-500 text-sm">{errors.email.message}</p>}
                    </div>
                    <div className="sm:col-span-2">
                      <label className="block text-sm font-medium text-gray-700">Subject</label>
                      <input {...register('subject')} className="input w-full" />
                    </div>
                    <div className="sm:col-span-2">
                      <label className="block text-sm font-medium text-gray-700">Message *</label>
                      <textarea {...register('message')} rows="4" className="input w-full" aria-required="true" aria-invalid={!!errors.message} />
                      {errors.message && <p className="text-red-500 text-sm">{errors.message.message}</p>}
                    </div>
                  </div>

                  <div className="mt-3 flex flex-col items-center gap-4">
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="bg-blue-600 text-white font-bold py-2 px-6 rounded shadow-md hover:shadow-lg hover:scale-105 transition duration-300 ease-in-out w-full sm:w-auto -mt-2"
                    >
                      {isSubmitting ? 'Sending...' : 'SUBMIT'}
                    </button>

                    <label htmlFor="recaptcha" className="sr-only">CAPTCHA verification</label>
                    <div className="recaptcha-wrapper">
                      <ReCAPTCHA
                        sitekey={import.meta.env.VITE_RECAPTCHA_SITE_KEY}
                        ref={recaptchaRef}
                        id="recaptcha"
                      />
                    </div>
                  </div>
                </motion.form>
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>
    </>
  );
}
